import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Radio, Loader2, CheckCircle2, XCircle, MessageSquare, Volume2, ShieldCheck
} from "lucide-react";
import axios from "axios";
import { Textarea } from "@/components/ui/textarea";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PUBLIC = axios.create({ baseURL: `${BACKEND}/api` });

export default function CommercialPreview() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const intendedAction = params.get("action");   // optional: 'approve' | 'reject'

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [pane, setPane] = useState(intendedAction === "reject" ? "reject" : "listen");

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await PUBLIC.get(`/public/commercials/preview/${token}`);
      setData(d);
      if (d.status && d.status !== "pending") setPane("done");
    } catch {
      setData({ unavailable: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token]);

  const decide = async (action) => {
    setBusy(true);
    try {
      await PUBLIC.post(`/public/commercials/preview/${token}/${action}`, { feedback });
      await load();
      setPane("done");
    } catch {
      // fall through — load() will reflect server state
      await load();
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Center>
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading preview…
      </Center>
    );
  }
  if (data?.unavailable) {
    return (
      <Center>
        <div className="text-center">
          <XCircle className="h-10 w-10 text-brand-onair mx-auto mb-3" />
          <h1 className="font-display text-2xl font-bold mb-1">Preview unavailable</h1>
          <p className="text-sm text-ink-muted">This link is no longer valid. Reply to the email and we&apos;ll send a fresh one.</p>
        </div>
      </Center>
    );
  }

  const st = data.station || {};
  const status = data.status;
  const isDone = status === "approved" || status === "rejected";

  return (
    <div className="min-h-screen bg-bg text-ink-primary" data-testid="preview-page">
      <header className="border-b border-line bg-bg-surface">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center gap-3">
          {st.logo_url ? (
            <img src={st.logo_url} alt="" className="h-10 w-10 rounded-md object-cover border border-line" />
          ) : (
            <div className="h-10 w-10 rounded-md bg-brand-amber/15 flex items-center justify-center">
              <Radio className="h-5 w-5 text-brand-amber" />
            </div>
          )}
          <div>
            <div className="font-display font-bold">{st.name || "Station"}</div>
            <div className="text-xs text-ink-muted">{st.tagline || ""}</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-amber/80 mb-3">// YOUR SPOT IS READY</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-[1.05]">
          {data.advertiser} <span className="text-ink-muted">·</span> <span className="text-brand-amber">{data.duration_sec}s</span>
        </h1>
        <p className="text-sm text-ink-muted mt-2">
          {data.sponsor_name ? `${data.sponsor_name}, take` : "Take"} a listen below and tell us if it&apos;s air-ready.
        </p>

        {/* Audio */}
        <section className="mt-6 rounded-xl border border-line bg-bg-surface p-5" data-testid="preview-audio-block">
          <div className="flex items-center gap-2 text-xs text-ink-muted mb-3">
            <Volume2 className="h-3.5 w-3.5 text-brand-amber" /> {data.duration_sec}s · {data.tone}
          </div>
          <audio controls src={data.audio_url} className="w-full" data-testid="preview-audio" preload="metadata" />
        </section>

        {/* Decision */}
        {isDone ? (
          <section className="mt-6 rounded-xl border border-line bg-bg-surface p-8 text-center" data-testid="preview-done">
            {status === "approved" ? (
              <>
                <div className="mx-auto h-14 w-14 rounded-full bg-emerald-500/15 flex items-center justify-center mb-3">
                  <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                </div>
                <h2 className="font-display text-2xl font-bold mb-1">Approved — thank you!</h2>
                <p className="text-sm text-ink-muted">{st.name} has been notified. Your spot will go to air on schedule.</p>
              </>
            ) : (
              <>
                <div className="mx-auto h-14 w-14 rounded-full bg-rose-500/15 flex items-center justify-center mb-3">
                  <MessageSquare className="h-8 w-8 text-rose-400" />
                </div>
                <h2 className="font-display text-2xl font-bold mb-1">Changes requested</h2>
                <p className="text-sm text-ink-muted">Your notes are on the way to the station team — they&apos;ll be in touch shortly.</p>
                {data.feedback && (
                  <div className="mt-3 mx-auto max-w-md rounded-md border border-line bg-bg-elevated p-3 text-xs text-ink-secondary italic">
                    &ldquo;{data.feedback}&rdquo;
                  </div>
                )}
              </>
            )}
          </section>
        ) : pane === "reject" ? (
          <section className="mt-6 rounded-xl border border-line bg-bg-surface p-6" data-testid="preview-reject-pane">
            <h2 className="font-display text-lg font-bold mb-2">Tell us what to change</h2>
            <p className="text-xs text-ink-muted mb-3">Be as specific as you like — voice, pacing, script tweaks, music vibe.</p>
            <Textarea
              rows={5}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="e.g. Voice is great. Can we mention 'family-owned since 1985' and slow down the CTA?"
              className="bg-bg border-line"
              data-testid="preview-feedback"
            />
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                onClick={() => setPane("listen")}
                data-testid="preview-cancel"
                className="rounded-md border border-line bg-bg-elevated px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary"
              >
                Cancel
              </button>
              <button
                onClick={() => decide("reject")}
                disabled={busy}
                data-testid="preview-submit-reject"
                className="inline-flex items-center gap-2 rounded-md bg-rose-500/90 hover:bg-rose-500 px-5 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquare className="h-4 w-4" />}
                Send notes
              </button>
            </div>
          </section>
        ) : (
          <section className="mt-6 grid sm:grid-cols-2 gap-3">
            <button
              onClick={() => decide("approve")}
              disabled={busy}
              data-testid="preview-approve"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 px-6 py-4 font-semibold text-emerald-300 disabled:opacity-50 transition"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
              Approve to air
            </button>
            <button
              onClick={() => setPane("reject")}
              data-testid="preview-open-reject"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-line bg-bg-surface hover:border-rose-400/40 hover:text-rose-300 px-6 py-4 font-semibold text-ink-secondary transition"
            >
              <MessageSquare className="h-5 w-5" />
              Request changes
            </button>
          </section>
        )}

        <div className="mt-8 flex items-center justify-center gap-2 text-[11px] text-ink-muted">
          <ShieldCheck className="h-3.5 w-3.5 text-brand-amber" />
          Secure preview — this link rotates each time the station resends.
        </div>
      </main>
    </div>
  );
}

function Center({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg text-ink-muted px-6">
      {children}
    </div>
  );
}
